import { useEffect, useState, useRef, useCallback } from 'react';
import { parseKmlToPolygons, bboxOf, classifyOverlaps } from '../../utils/kmlImport';
import {
  buildingsInBboxComplete, createBuilding, deleteBuilding,
} from '../../lib/buildingsApi';

// Full-screen modal flow for "Import KML": pick file -> parse + complete bbox
// load -> visible preflight -> classify against existing buildings -> show an
// honest summary -> the operator picks Replace / Skip / Cancel -> orchestrate
// bounded-concurrency writes against the existing RPCs (createBuilding /
// deleteBuilding), reload the map, close. Pure orchestration on top of
// kmlImport.js + buildingsApi - no new server surface.

const BACKDROP = {
  position: 'fixed',
  inset: 0,
  zIndex: 50,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'rgba(2,6,23,0.55)',
  backdropFilter: 'blur(2px)',
  WebkitBackdropFilter: 'blur(2px)',
  direction: 'rtl',
};

const CARD = {
  width: 'min(520px, 92vw)',
  maxHeight: '88vh',
  overflowY: 'auto',
  display: 'flex',
  flexDirection: 'column',
  gap: 14,
  padding: '20px 22px',
  background: 'linear-gradient(135deg, rgba(15,23,42,0.97) 0%, rgba(30,41,59,0.95) 100%)',
  borderRadius: 14,
  border: '1px solid rgba(148,163,184,0.26)',
  borderTop: '3px solid #0d9488',
  boxShadow:
    '0 1px 0 rgba(255,255,255,0.07) inset, 0 18px 40px rgba(2,6,23,0.7), 0 1px 3px rgba(0,0,0,0.5)',
  color: '#e2e8f0',
  fontFamily: "'Vazirmatn', sans-serif",
};

const TITLE = {
  fontSize: 15,
  fontWeight: 800,
  color: '#5eead4',
  letterSpacing: '0.01em',
  lineHeight: 1.3,
};

const LINE = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'baseline',
  gap: 8,
  fontSize: 13,
  lineHeight: 1.6,
};
const LABEL = { color: '#94a3b8', fontWeight: 500 };
const VAL = { color: '#e2e8f0', fontWeight: 700, direction: 'ltr' };

const BTN_BASE = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '9px 14px',
  fontFamily: "'Vazirmatn', sans-serif",
  fontSize: 13,
  fontWeight: 700,
  lineHeight: 1,
  borderRadius: 8,
  cursor: 'pointer',
  transition: 'background 140ms ease, color 140ms ease, transform 140ms ease',
};
const BTN_PRIMARY = {
  ...BTN_BASE,
  flex: 1,
  background: 'linear-gradient(135deg, #0d9488 0%, #0f766e 100%)',
  color: '#f0fdfa',
  border: '1px solid rgba(45,212,191,0.42)',
  boxShadow: '0 1px 0 rgba(255,255,255,0.12) inset',
};
const BTN_AMBER = {
  ...BTN_BASE,
  flex: 1,
  background: 'linear-gradient(135deg, #d97706 0%, #b45309 100%)',
  color: '#fffbeb',
  border: '1px solid rgba(251,191,36,0.42)',
  boxShadow: '0 1px 0 rgba(255,255,255,0.14) inset',
};
const BTN_GHOST = {
  ...BTN_BASE,
  flex: 1,
  background: 'rgba(51,65,85,0.55)',
  color: '#e2e8f0',
  border: '1px solid rgba(148,163,184,0.32)',
};

function Row({ label, value, accent }) {
  return (
    <div style={LINE}>
      <span style={LABEL}>{label}</span>
      <span style={{ ...VAL, color: accent || VAL.color }}>{value}</span>
    </div>
  );
}

// Internal reasons -> short Persian labels for the invalid-list summary.
const REASON_FA = {
  'parse-failed': 'فایل KML قابل خواندن نبود',
  'invalid-ring': 'مختصاتِ نامعتبر',
  'self-intersecting': 'شکلِ خودبرخوردکننده',
  'invalid-shape': 'هندسه‌ی نامعتبر',
  'unsupported-geometry': 'نوعِ هندسه پشتیبانی نمی‌شود',
};

const MAX_VALID_POLYGONS = 5000;
const WRITE_CONCURRENCY = 8;
const faNumber = (n) => Number(n).toLocaleString('fa-IR');

// Execute already-classified import items with a hard worker limit. This is
// exported only so the concurrency/progress contract can be tested without
// reaching into React state. Each worker finishes an item's delete/create
// sequence before taking another item; no more than eight network requests can
// therefore be active at once.
export async function runKmlImportQueue({
  items,
  isCancelled = () => false,
  onSettled = () => {},
  create = createBuilding,
  remove = deleteBuilding,
}) {
  let added = 0;
  let replaced = 0;
  let failed = 0;
  let done = 0;
  let next = 0;
  let firstError = null;
  const failureCounts = new Map();

  const runOne = async ({ item, isReplace }) => {
    try {
      if (isReplace) {
        for (const id of (item.overlapIds || [])) {
          const response = await remove(id);
          if (response && response.error) {
            throw new Error(response.error.message || 'delete failed');
          }
        }
      }
      const response = await create(item.geometry);
      if (response && response.error) {
        throw new Error(response.error.message || 'create failed');
      }
      if (isReplace) replaced += 1; else added += 1;
    } catch (e) {
      failed += 1;
      const message = (e && e.message) ? e.message : String(e);
      if (!firstError) firstError = message;
      failureCounts.set(message, (failureCounts.get(message) || 0) + 1);
    } finally {
      done += 1;
      onSettled(done, items.length);
    }
  };

  const worker = async () => {
    while (!isCancelled()) {
      const index = next;
      if (index >= items.length) return;
      next += 1;
      await runOne(items[index]);
    }
  };

  const workerCount = Math.min(WRITE_CONCURRENCY, items.length);
  await Promise.all(Array.from({ length: workerCount }, () => worker()));
  return {
    added,
    replaced,
    failed,
    done,
    firstError,
    failureCounts: Object.fromEntries(failureCounts),
    cancelled: isCancelled(),
  };
}

export default function KmlImportPanel({ onClose, onCompleted }) {
  // 'pick' -> 'analyzing' -> 'preflight' -> 'summary' -> 'executing' ->
  // 'done' (or 'error').
  const [state, setState] = useState('pick');
  const [fileName, setFileName] = useState(null);
  const [classification, setClassification] = useState(null);
  const [invalid, setInvalid] = useState([]);
  const [parsedPolygons, setParsedPolygons] = useState([]);
  const [existingRows, setExistingRows] = useState([]);
  const [preflight, setPreflight] = useState(null);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [result, setResult] = useState(null);
  const [errorMsg, setErrorMsg] = useState(null);
  const fileInputRef = useRef(null);
  const cancelRef = useRef(false);

  useEffect(() => () => { cancelRef.current = true; }, []);

  // Trigger the hidden picker as soon as the panel opens (pick state). The
  // user clicked "بارگذاری KML" expecting a picker - skip an extra click.
  useEffect(() => {
    if (state === 'pick' && fileInputRef.current) fileInputRef.current.click();
  }, [state]);

  const onFile = useCallback(async (file) => {
    if (!file) { onClose(); return; }
    cancelRef.current = false;
    setFileName(file.name);
    setState('analyzing');
    setErrorMsg(null);
    setClassification(null);
    setPreflight(null);

    let text;
    try { text = await file.text(); }
    catch (e) { setErrorMsg('خواندنِ فایل ناموفق بود'); setState('error'); return; }

    let parsed;
    try { parsed = parseKmlToPolygons(text); }
    catch (e) { setErrorMsg('خواندنِ فایل KML ناموفق بود'); setState('error'); return; }
    const valid = parsed.valid || [];
    const bad = parsed.invalid || [];
    const holesDropped = Number(parsed.holesDropped) || 0;
    setInvalid(bad);
    setParsedPolygons(valid);
    setExistingRows([]);

    // Refuse city-scale files immediately. Besides avoiding the overlap pass,
    // this deliberately avoids even loading the bbox: the bulk path belongs to
    // operations, and there is no useful work this hand-drawn import panel can
    // offer for 5,001+ polygons.
    if (valid.length > MAX_VALID_POLYGONS) {
      setPreflight({
        validCount: valid.length,
        totalCount: valid.length + bad.length,
        holesDropped,
        existingCount: null,
        overLimit: true,
        saturated: false,
      });
      setState('preflight');
      return;
    }

    const b = bboxOf(valid);
    let existing = [];
    let saturated = false;
    if (b) {
      try {
        const complete = await buildingsInBboxComplete(b);
        if (complete.error) {
          setErrorMsg('خواندنِ ساختمان‌های موجود ناموفق بود');
          setState('error');
          return;
        }
        existing = complete.rows || [];
        saturated = complete.saturated;
      } catch (e) {
        setErrorMsg('خواندنِ ساختمان‌های موجود ناموفق بود');
        setState('error');
        return;
      }
    }
    if (cancelRef.current) return;

    setExistingRows(existing);
    setPreflight({
      validCount: valid.length,
      totalCount: valid.length + bad.length,
      holesDropped,
      existingCount: existing.length,
      overLimit: false,
      saturated,
    });
    setState('preflight');
  }, [onClose]);

  const continueFromPreflight = useCallback(() => {
    if (!preflight || preflight.overLimit || preflight.saturated) return;
    const cls = classifyOverlaps(parsedPolygons, existingRows);
    setClassification(cls);
    setState('summary');
  }, [existingRows, parsedPolygons, preflight]);

  const run = useCallback(async (mode) => {
    if (!classification) return;
    const { clean, replaceable } = classification;
    // 'skip' imports only clean polygons; 'replace' also deletes overlapping
    // rows the caller can manage and then inserts the replacement polygon.
    const toReplace = mode === 'replace' ? replaceable : [];
    const items = [
      ...clean.map((item) => ({ item, isReplace: false })),
      ...toReplace.map((item) => ({ item, isReplace: true })),
    ];
    const total = items.length;
    setProgress({ done: 0, total });
    setState('executing');

    const execution = await runKmlImportQueue({
      items,
      isCancelled: () => cancelRef.current,
      onSettled: (done) => setProgress({ done, total }),
    });
    if (execution.cancelled) return;

    setResult({
      added: execution.added,
      replaced: execution.replaced,
      skipped: classification.blocked.length + (mode === 'skip' ? replaceable.length : 0),
      failed: execution.failed,
      invalid: invalid.length,
      firstError: execution.firstError,
      failureCounts: execution.failureCounts,
    });
    setState('done');
    if (onCompleted) onCompleted();
  }, [classification, invalid, onCompleted]);

  const close = useCallback(() => {
    cancelRef.current = true;
    onClose();
  }, [onClose]);

  return (
    <div style={BACKDROP} role="dialog" aria-modal="true" aria-label="بارگذاری KML">
      <div style={CARD}>
        <div style={TITLE}>بارگذاری فایل KML</div>

        <input
          ref={fileInputRef}
          type="file"
          accept=".kml,application/vnd.google-earth.kml+xml,application/xml,text/xml"
          style={{ display: 'none' }}
          onChange={(e) => onFile(e.target.files && e.target.files[0])}
          // Native cancel event on file inputs (Chrome 113+, all modern
          // browsers). Without this, dismissing the OS file dialog left the
          // modal stuck in the "pick" state until the user refocused the tab.
          // The visible "بستن" button below is the always-available fallback.
          onCancel={close}
        />

        {state === 'pick' && (
          <>
            <div style={{ fontSize: 12.5, color: '#cbd5e1', lineHeight: 1.6 }}>
              در حال باز کردن پنجره‌ی انتخاب فایل…
            </div>
            <div style={{ fontSize: 11.5, color: '#94a3b8', lineHeight: 1.6 }}>
              اگر پنجره را بدون انتخاب فایل بستید، روی «بستن» بزنید.
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button type="button" style={BTN_GHOST} onClick={close}>بستن</button>
            </div>
          </>
        )}

        {state === 'analyzing' && (
          <div style={{ fontSize: 12.5, color: '#cbd5e1', lineHeight: 1.7 }}>
            در حال خواندن
            {fileName ? ` «${fileName}» ` : ' '}
            و شمارش ساختمان‌های موجود…
          </div>
        )}

        {state === 'preflight' && preflight && (
          <>
            <div
              style={{
                fontSize: 12.5,
                color: '#cbd5e1',
                background: 'rgba(2,6,23,0.4)',
                border: '1px solid rgba(148,163,184,0.18)',
                borderRadius: 8,
                padding: '10px 12px',
                lineHeight: 1.6,
                display: 'flex',
                flexDirection: 'column',
                gap: 5,
              }}
            >
              <Row label="کل پولیگان‌های فایل" value={faNumber(preflight.totalCount)} />
              <Row label="پولیگان‌های معتبر" value={faNumber(preflight.validCount)} accent="#5eead4" />
              <Row label="ردشده" value={faNumber(invalid.length)} accent={invalid.length ? '#f87171' : null} />
              {Object.entries(
                invalid.reduce((counts, item) => {
                  counts[item.reason] = (counts[item.reason] || 0) + 1;
                  return counts;
                }, {}),
              ).map(([reason, count]) => (
                <Row
                  key={reason}
                  label={`ردشده: ${REASON_FA[reason] || reason}`}
                  value={faNumber(count)}
                  accent="#f87171"
                />
              ))}
              <Row label="حفره‌های داخلی حذف‌شده" value={faNumber(preflight.holesDropped)} accent="#fbbf24" />
              <Row
                label="ساختمان‌های موجود در محدوده"
                value={preflight.existingCount == null ? 'بررسی نشد' : faNumber(preflight.existingCount)}
              />
            </div>

            {preflight.overLimit && (
              <div
                style={{
                  fontSize: 12.5,
                  fontWeight: 600,
                  color: '#fca5a5',
                  background: 'rgba(127,29,29,0.32)',
                  border: '1px solid rgba(248,113,113,0.32)',
                  borderRadius: 8,
                  padding: '8px 10px',
                  lineHeight: 1.8,
                }}
              >
                {`این فایل ${faNumber(preflight.validCount)} پولیگان معتبر دارد و از سقف ${faNumber(MAX_VALID_POLYGONS)} پولیگان بیشتر است. این پنل برای پولیگان‌هایی است که با دست در Google Earth ترسیم شده‌اند. مجموعه‌داده‌ی کل شهر را تیم عملیات از مسیر بارگذاری انبوه وارد می‌کند.`}
              </div>
            )}

            {preflight.saturated && (
              <div
                style={{
                  fontSize: 12.5,
                  fontWeight: 600,
                  color: '#fca5a5',
                  background: 'rgba(127,29,29,0.32)',
                  border: '1px solid rgba(248,113,113,0.32)',
                  borderRadius: 8,
                  padding: '8px 10px',
                  lineHeight: 1.8,
                }}
              >
                تراکم ساختمان‌ها حتی در کوچک‌ترین کاشی بیش از ظرفیت خواندن است؛
                برای جلوگیری از نتیجه‌ی نادرست، ادامه ممکن نیست.
              </div>
            )}

            <div style={{ display: 'flex', gap: 8 }}>
              {!preflight.overLimit && !preflight.saturated && (
                <button type="button" style={BTN_PRIMARY} onClick={continueFromPreflight}>ادامه</button>
              )}
              <button type="button" style={BTN_GHOST} onClick={close}>بستن</button>
            </div>
          </>
        )}

        {state === 'error' && (
          <>
            <div
              style={{
                fontSize: 12.5,
                fontWeight: 600,
                color: '#fca5a5',
                background: 'rgba(127,29,29,0.32)',
                border: '1px solid rgba(248,113,113,0.32)',
                borderRadius: 8,
                padding: '8px 10px',
                lineHeight: 1.6,
              }}
            >
              {errorMsg || 'خطا'}
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button type="button" style={BTN_GHOST} onClick={close}>بستن</button>
            </div>
          </>
        )}

        {state === 'summary' && classification && (
          <>
            <div
              style={{
                fontSize: 12.5,
                color: '#cbd5e1',
                background: 'rgba(2,6,23,0.4)',
                border: '1px solid rgba(148,163,184,0.18)',
                borderRadius: 8,
                padding: '10px 12px',
                lineHeight: 1.6,
                display: 'flex',
                flexDirection: 'column',
                gap: 5,
              }}
            >
              <Row
                label="کل پولیگان‌های فایل"
                value={(classification.clean.length + classification.replaceable.length
                  + classification.blocked.length + invalid.length).toString()}
              />
              <Row
                label="قابلِ افزودن بدون تداخل"
                value={classification.clean.length.toString()}
                accent="#5eead4"
              />
              <Row
                label="با ساختمان‌های قابلِ مدیریت تداخل دارد"
                value={classification.replaceable.length.toString()}
                accent="#fbbf24"
              />
              <Row
                label="با ساختمانِ اپراتورِ دیگر تداخل دارد (پرش)"
                value={classification.blocked.length.toString()}
                accent="#f87171"
              />
              {invalid.length > 0 && (
                <Row
                  label="نامعتبر (حذف‌شد)"
                  value={invalid.length.toString()}
                  accent="#94a3b8"
                />
              )}
            </div>

            {classification.clean.length + classification.replaceable.length === 0 ? (
              <div style={{ fontSize: 12.5, color: '#94a3b8', lineHeight: 1.6 }}>
                چیزی برای افزودن وجود ندارد.
              </div>
            ) : null}

            {invalid.length > 0 && (
              <details style={{ fontSize: 12, color: '#94a3b8' }}>
                <summary style={{ cursor: 'pointer' }}>چرا برخی پولیگان‌ها نامعتبر بودند؟</summary>
                <ul style={{ margin: '6px 16px 0', padding: 0 }}>
                  {Object.entries(
                    invalid.reduce((m, x) => { m[x.reason] = (m[x.reason] || 0) + 1; return m; }, {}),
                  ).map(([reason, n]) => (
                    <li key={reason}>{`${n}: ${REASON_FA[reason] || reason}`}</li>
                  ))}
                </ul>
              </details>
            )}

            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {classification.replaceable.length > 0 && (
                <button type="button" style={BTN_AMBER} onClick={() => run('replace')}>
                  جایگزینی همپوشان‌ها
                </button>
              )}
              {(classification.clean.length > 0 || classification.replaceable.length > 0) && (
                <button type="button" style={BTN_PRIMARY} onClick={() => run('skip')}>
                  پرش از همپوشان‌ها
                </button>
              )}
              <button type="button" style={BTN_GHOST} onClick={close}>انصراف</button>
            </div>
          </>
        )}

        {state === 'executing' && (
          <>
            <div style={{ fontSize: 13, color: '#cbd5e1', lineHeight: 1.6 }}>
              {`${progress.done} از ${progress.total} انجام شد…`}
            </div>
            <div
              style={{
                position: 'relative',
                height: 6,
                background: 'rgba(51,65,85,0.6)',
                borderRadius: 4,
                overflow: 'hidden',
              }}
            >
              <div
                style={{
                  position: 'absolute',
                  inset: 0,
                  background: 'linear-gradient(90deg, #0d9488, #2dd4bf)',
                  width: progress.total > 0
                    ? `${Math.round((progress.done / progress.total) * 100)}%`
                    : '0%',
                  transition: 'width 140ms ease',
                }}
              />
            </div>
          </>
        )}

        {state === 'done' && result && (
          <>
            <div
              style={{
                fontSize: 12.5,
                color: '#cbd5e1',
                background: 'rgba(2,6,23,0.4)',
                border: '1px solid rgba(148,163,184,0.18)',
                borderRadius: 8,
                padding: '10px 12px',
                lineHeight: 1.6,
                display: 'flex',
                flexDirection: 'column',
                gap: 5,
              }}
            >
              <Row label="افزوده شد" value={String(result.added)} accent="#5eead4" />
              {result.replaced > 0 && <Row label="جایگزین شد" value={String(result.replaced)} accent="#fbbf24" />}
              {result.skipped > 0 && <Row label="پرش‌شده" value={String(result.skipped)} />}
              {result.invalid > 0 && <Row label="نامعتبر" value={String(result.invalid)} />}
              {Object.entries(result.failureCounts || {}).map(([message, count]) => (
                <div key={message} style={{ fontSize: 12, color: '#f87171', lineHeight: 1.7 }}>
                  {`${faNumber(count)} مورد به‌دلیل ${message} رد شد`}
                </div>
              ))}
            </div>
            {result.failed > 0 && result.firstError && (
              <div
                style={{
                  fontSize: 11.5,
                  color: '#fca5a5',
                  background: 'rgba(127,29,29,0.32)',
                  border: '1px solid rgba(248,113,113,0.32)',
                  borderRadius: 8,
                  padding: '8px 10px',
                  lineHeight: 1.6,
                  direction: 'ltr',
                  textAlign: 'left',
                  fontFamily: "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace",
                  wordBreak: 'break-word',
                }}
                title="پیام خطای سرور برای اولین مورد ناموفق"
              >
                {result.firstError}
              </div>
            )}
            <div style={{ display: 'flex', gap: 8 }}>
              <button type="button" style={BTN_PRIMARY} onClick={close}>بستن</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
